import { useState, useMemo } from 'react';
import {
  Form, Input, Toggle,
} from '@skyline-platform/alchemy';
import PropTypes from 'prop-types';
import '../FullScreenForm.css';
import './ApiKeyForm.css';
import { validateStringLength } from '@utils/formValidators';
import * as is from 'is';
import {
  saveApiKey,
  updateApiKey,
} from '@utils/graphql/creators/apiKeys';
import { useFormSubmit } from '@utils/react/hooks/useFormSubmit';
import { FormFooter } from '../FormFooter/FormFooter';
import { SearchExistingZone } from '@components/FormComponents/ZoneForm/SearchExistingZone';
import { SearchExistingContact } from '@components/FormComponents/ContactForm/SearchExistingContact';

export const ApiKeyForm = ({
  apiKeyData,
  handleClose,
  setNewCreatedApiKey,
}) => {
  const [description, setDescription] = useState(apiKeyData?.description || '');
  const [canReadWrite, setCanReadWrite] = useState(apiKeyData?.can_read_write ?? false);
  const [canRequestSnap, setCanRequestSnap] = useState(apiKeyData?.can_request_snap ?? false);
  const [canUseSrs, setCanUseSrs] = useState(apiKeyData?.can_use_srs ?? false);
  const [canSetTokenExpiration, setCanSetTokenExpiration] = useState(apiKeyData?.can_set_token_expiration ?? false);
  const [canViewPrivateMetadata, setcanViewPrivateMetadata] = useState(apiKeyData?.can_view_private_metadata ?? false);
  const [zoneId, setZoneId] = useState(apiKeyData?.zone?.id || '');
  const [contactId, setContactId] = useState(apiKeyData?.contact.id || '');
  const [formData, setFormData] = useState(apiKeyData || {});

  const error = useMemo(() => {
    // Stryker disable next-line StringLiteral
    const descValid = validateStringLength(description, 0, 140, 'Description');
    return {
      description: {
        isValid: descValid.isValid,
        error: descValid.error,
      },
    };
  }, [description]);

  const allFieldsValid = useMemo(() => {
    // Stryker disable next-line StringLiteral
    const descValid = validateStringLength(description, 0, 140, 'Description');
    const zoneValid = !is.empty(zoneId);
    const contactValid = !is.empty(contactId);
    return descValid.isValid && zoneValid && contactValid;
  }, [
    description,
    zoneId,
    contactId,
  ]);

  const submitApiKey = useFormSubmit({
    saveCreator: (payload) => saveApiKey('create_api_key', payload),
    updateCreator: (id, payload) => updateApiKey('update_api_key', id, payload),
    existingId: apiKeyData?.id,
    addReturnProperties: (mutation) => mutation.addReturnProperties('id', 'user_key'),
    logMessages: { success: 'Successfully created API Key.', fail: 'Failed to create API Key.' },
    updateLogMessages: { success: 'Successfully updated API Key.', fail: 'Failed to edit API Key.' },
    onSuccess: (data) => {
      if (typeof setNewCreatedApiKey === 'function') setNewCreatedApiKey(data);
    },
    handleClose,
  });

  const handleApiKey = () => {
    const payload = {
      description,
      zoneId,
      contactId,
      canReadWrite,
      canRequestSnap,
      canUseSrs,
      canSetTokenExpiration,
      canViewPrivateMetadata,
    };
    if (!apiKeyData) {
      payload.createdDate = new Date().toISOString().slice(0, 19).replace('T', ' ');
    }
    return submitApiKey(payload);
  };

  return (
    <gar-api-key-form>
      <Form
        formTitle='Create New ApiKey'
        footerPlacement='right'
        footer={[
          <FormFooter
            key='footer'
            onCancel={handleClose}
            onSubmit={handleApiKey}
            submitLabel={apiKeyData ? 'Update API Key' : 'Create New API Key'}
            isValid={allFieldsValid}
          />,
        ]}
      >
        <Input
          id='description'
          inputLabel='Description'
          helperText='Description of purpose and recipient of the API key (e.g., application, team, or use case) '
          onChange={(e) => { setDescription(e.target.value); }}
          value={description}
          type='text'
          multiline
          rows={6}
          // Stryker disable next-line StringLiteral
          errorMessage={error.description.isValid ? '' : error.description.error}
        />
        <SearchExistingContact
          formData={formData?.contact}
          setFormData={(contactData) => {
            setContactId(contactData.id);
            setFormData((prevData) => {
              return {
                ...prevData,
                contact: contactData,
              };
            });
          }}
        />
        <SearchExistingZone
          formData={formData?.zone}
          setFormData={(zoneData) => {
            setZoneId(zoneData.id);
          }}
        />
        <h3>
          Permissions
        </h3>
        <Toggle
          label='Online/Offline a stream'
          checked={canReadWrite}
          handleChange={(e) => setCanReadWrite(e.target.checked)}
        />
        <div className='toggle-article'>
          <Toggle
            label='Can Request Snapshot'
            checked={canRequestSnap}
            handleChange={(e) => setCanRequestSnap(e.target.checked)}
          />
          {canRequestSnap && <article
            aria-hidden={!canRequestSnap}
            className='warning wrap'>
            <i className='fa-solid fa-circle-exclamation' />
            This requires a Snapshot Server to be configured.
          </article>}
        </div>
        <div className='toggle-article'>
          <Toggle
            label='Can Record'
            checked={canUseSrs}
            handleChange={(e) => setCanUseSrs(e.target.checked)}
          />
          {canUseSrs && <article
            aria-hidden={!canUseSrs}
            className='warning'>
            <i className='fa-solid fa-circle-exclamation' />
            This requires an SRS (Stream Recording Service) to be configured.
          </article>}
        </div>
        <div className='toggle-article'>
          <Toggle
            label='Can Set Token Expiration'
            checked={canSetTokenExpiration}
            handleChange={(e) => setCanSetTokenExpiration(e.target.checked)}
          />
          {canSetTokenExpiration && <article
            aria-hidden={!canSetTokenExpiration}
            className='warning wrap'>
            <i className='fa-solid fa-circle-exclamation' />
            This requires the Stream Authorization feature to be enabled.
          </article>}
        </div>
        <div className='toggle-article'>
          <Toggle
            label='Can View Secure Data'
            checked={canViewPrivateMetadata}
            handleChange={(e) => setcanViewPrivateMetadata(e.target.checked)}
          />
          {canViewPrivateMetadata && <article
            aria-hidden={!canViewPrivateMetadata}
            className='warning wrap'>
            <i className='fa-solid fa-circle-exclamation' />
            Allows access to view secure camera data and private metadata when using the getAll, getById, and getByName requests
          </article>}
        </div>
      </Form>
    </gar-api-key-form>
  );
};

ApiKeyForm.propTypes = {
  apiKeyData: PropTypes.object,
  handleClose: PropTypes.func,
  setFormData: PropTypes.func,
  setNewCreatedApiKey: PropTypes.func,
};